import React, { useCallback } from 'react';
import Link from 'next/link';
import {
  Configure,
  InstantSearch,
  useInstantSearch,
  useHits
} from 'react-instantsearch';
import IconTrendUp from 'assets/icons/ic-trend-up-01.svg';
import locationStore from 'stores/LocationStore';
import {
  ALGOLIA_INDEX_NAME_QUERY_SUGGESTIONS,
  labelsAnalyticsTags
} from 'constants/algolia';
import searchClient, { createAnalyticsTags } from 'utils/algolia';
import { createLinkToRedirect } from 'utils/createLinkToRedirect';
import { analytics, analyticsEvents, transportMethods } from 'lib/analytics';

import * as styles from './AlgoliaPopularSearch.css';

type HitProps = {
  hit: {
    query: string;
  };
  hasMossColor?: boolean;
  onClose?: () => void;
  setInputValue?: (value: string) => void;
};

function Hit({ hit, hasMossColor, onClose, setInputValue }: HitProps) {
  const query = hit?.query;
  const userLocation = locationStore?.selectedLocation;

  const textClassName = `${styles.textQuery} ${
    hasMossColor ? styles.textQueryMoss : ''
  }`;

  const iconClassName = `${styles.iconStyle} ${
    hasMossColor ? styles.iconStyleMoss : ''
  }`;

  const handleClickSearch = useCallback(() => {
    analytics.sendEvent(analyticsEvents.search_recommended_clicked, {
      search_recommendation_popular_searchs_clicked: query,
      transport: transportMethods.sendBeacon
    });
    if (setInputValue) setInputValue(query);
    if (onClose) onClose();
  }, [query, setInputValue, onClose]);

  return (
    <Link
      href={createLinkToRedirect({
        param: `?s=${query}`,
        routeType: 'search-bar-default-filter',
        userLocation
      })}
      className={styles.ContentHit}
      onClick={handleClickSearch}
    >
      <IconTrendUp
        width={16}
        height={16}
        className={iconClassName}
        aria-hidden="true"
      />
      <p className={textClassName}>{`${query
        .charAt(0)
        .toUpperCase()}${query.substring(1)}`}</p>
    </Link>
  );
}

type SuggestionHit = {
  objectID: string;
  query: string;
};

function SuggestionsSection({
  hasMossColor,
  onClose,
  setInputValue
}: {
  hasMossColor: boolean;
  onClose?: () => void;
  setInputValue?: (value: string) => void;
}) {
  const { results } = useInstantSearch();
  const { items: hits, sendEvent } = useHits<SuggestionHit>();

  if (!results?.nbHits) return null;

  return (
    <div
      className={styles.popularSearchContainer}
      data-testid="search-algolia-dropdown-suggestions"
    >
      <h3 className={styles.title}>Pesquisas populares</h3>
      <ul className={styles.HitsList}>
        {hits.map((hit) => (
          <li
            key={hit.objectID}
            onClick={() => sendEvent('click', hit, 'Hit Clicked')}
            style={{ listStyle: 'none' }}
          >
            <Hit
              hit={hit}
              hasMossColor={hasMossColor}
              onClose={onClose}
              setInputValue={setInputValue}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AlgoliaPopularSearch({
  userTokenAlgolia,
  hasMossColor,
  onClose,
  setInputValue,
  query,
  labelTagsAnalytics
}: {
  userTokenAlgolia: string;
  hasMossColor: boolean;
  onClose?: () => void;
  setInputValue?: (value: string) => void;
  query: string;
  labelTagsAnalytics?: string[];
}) {
  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={ALGOLIA_INDEX_NAME_QUERY_SUGGESTIONS}
      insights={true}
    >
      <Configure
        hitsPerPage={5}
        query={query}
        userToken={userTokenAlgolia}
        clickAnalytics={true}
        analytics={true}
        analyticsTags={createAnalyticsTags({
          groupTag: labelsAnalyticsTags.recommendationSearch,
          componentTag: labelsAnalyticsTags.popularSearch,
          otherTags: labelTagsAnalytics
        })}
      />
      <SuggestionsSection
        hasMossColor={hasMossColor}
        onClose={onClose}
        setInputValue={setInputValue}
      />
    </InstantSearch>
  );
}
